import { ApolloClient } from "@apollo/client";
import { NormalizedCacheObject } from "@apollo/client";
import { getAuth } from "@clerk/nextjs/server";
import { GetServerSidePropsContext, NextPage } from "next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import FullContainerLoader from "@/components/common/loaders/fullContainerLoader";
import { useAnalytics } from "@/components/common/segment/AnalyticsContext";
import CustomBookingThemeProvider from "@/config/customBookingThemeProvider";
import { StripeProvider } from "@/config/stripe";
import { ClientBookingContextProvider } from "@/contexts/clientBookingContext";
import {
  MedspaInfoDocument,
  MedspaInfoQueryResult,
} from "@/graphql/queries/medspaInfo.graphql.types";
import { SelectiveBookingLinkQueryResult } from "@/graphql/queries/onlineBooking/selectiveBookingLinks.graphql.types";
import { SelectiveBookingLinkDocument } from "@/graphql/queries/onlineBooking/selectiveBookingLinks.graphql.types";
import { MedspaOnlineStoreDataDocument } from "@/graphql/queries/onlineStore/medspaOnlineStoreData.graphql.types";
import { MedspaOnlineStoreDataQueryResult } from "@/graphql/queries/onlineStore/medspaOnlineStoreData.graphql.types";
import { useEffectOnce } from "@/hooks/misc/useEffectOnce";
import { addApolloState, initializeApollo } from "@/lib/apollo";
import { calculateBookingCustomizationEnabled } from "@/utils";
import { getDefaultBookingState } from "@/utils/booking";
import { FeatureFlags } from "@/utils/featureFlags";
import { MedspaOnlineStoreData } from "@/views/online-store/onlineStoreView";

type BookingPageProps = {
  medspaInfo: MedspaInfoQueryResult["data"]["medspa"][0];
  defaultState: ReturnType<typeof getDefaultBookingState>;
  packagesInfo: MedspaOnlineStoreData["packages"] | null;
  selectiveBookingLinkData: SelectiveBookingLinkQueryResult["data"] | null;
};

async function fetchMedspaData(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  slug: string
) {
  try {
    const { data } = await apolloClient.query<MedspaInfoQueryResult["data"]>({
      query: MedspaInfoDocument,
      variables: { slug },
      context: { noAuth: true },
    });

    if (!data.medspa.length) {
      throw new Error("Medspa not found");
    }

    return data.medspa[0];
  } catch (error) {
    console.error("Error fetching medspa data:", error);
    throw error;
  }
}

async function fetchPackagesInfo(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  slug: string
) {
  const { data: medspaOnlineStoreData } = await apolloClient.query<
    MedspaOnlineStoreDataQueryResult["data"]
  >({
    query: MedspaOnlineStoreDataDocument,
    variables: { slug },
    context: { noAuth: true },
  });

  return medspaOnlineStoreData?.medspa[0].packages ?? null;
}

async function fetchSelectiveBookingLink(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  selectiveBookingLinkId: string
) {
  const { data: selectiveBookingLink } = await apolloClient.query<
    SelectiveBookingLinkQueryResult["data"]
  >({
    query: SelectiveBookingLinkDocument,
    variables: { id: selectiveBookingLinkId },
    context: { noAuth: true },
  });

  return selectiveBookingLink;
}

const BookingView = dynamic(() => import("@/views/booking/bookingView"), {
  ssr: false,
  loading: () => <FullContainerLoader />,
});

const Booking: NextPage<BookingPageProps> = ({
  medspaInfo,
  defaultState,
  packagesInfo,
  selectiveBookingLinkData,
}) => {
  const analytics = useAnalytics();
  const customizationEnabled = calculateBookingCustomizationEnabled(medspaInfo);
  const { customizationPrimaryColor } = medspaInfo.configuration;
  const { pathname } = useRouter();

  useEffectOnce(!!analytics, () => {
    analytics.page(
      "",
      { moxiePath: pathname },
      { context: { groupId: medspaInfo.id } }
    );
  });

  return (
    <CustomBookingThemeProvider
      customizationEnabled={customizationEnabled}
      customizationPrimaryColor={customizationPrimaryColor}
    >
      <ClientBookingContextProvider
        medspaData={medspaInfo}
        defaultState={defaultState}
        packagesInfo={packagesInfo}
        selectiveBookingLinkData={selectiveBookingLinkData}
      >
        <StripeProvider>
          <BookingView />
        </StripeProvider>
      </ClientBookingContextProvider>
    </CustomBookingThemeProvider>
  );
};

Booking.disablePageTracking = true;
export default Booking;

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  try {
    const { slug, selectiveBookingLinkId } = ctx.query;
    const { getToken: getClerkToken } = getAuth(ctx.req);
    const apolloClient = initializeApollo(null, getClerkToken);

    const medspaInfo = await fetchMedspaData(apolloClient, slug as string);

    const packagesSuggestionsV1Enabled =
      medspaInfo.additionalPublicInfo.enabledFeatures.includes(
        FeatureFlags.PACKAGES_SUGGESTIONS_V1
      );
    const selectiveBookingLinkEnabled =
      medspaInfo.additionalPublicInfo.enabledFeatures.includes(
        FeatureFlags.LAST_MINUTE_APPOINTMENTS_V1
      );

    // Fetch additional data based on feature flags
    const packagesInfo = packagesSuggestionsV1Enabled
      ? await fetchPackagesInfo(apolloClient, slug as string)
      : null;

    const selectiveBookingLinkData =
      selectiveBookingLinkEnabled && selectiveBookingLinkId
        ? await fetchSelectiveBookingLink(
            apolloClient,
            selectiveBookingLinkId as string
          )
        : null;

    const bookingCustomizationEnabled =
      medspaInfo.configuration?.groupServiceMenuItemsByCategoriesEnabled;
    const newMedspaInfo = structuredClone(medspaInfo);
    newMedspaInfo.configuration = {
      ...medspaInfo.configuration,
      groupServiceMenuItemsByCategoriesEnabled: bookingCustomizationEnabled,
    };

    return addApolloState(apolloClient, {
      props: {
        medspaInfo: newMedspaInfo,
        defaultState: getDefaultBookingState(medspaInfo, ctx.query),
        packagesInfo,
        selectiveBookingLinkData,
      },
    });
  } catch (error) {
    console.error("Error in getServerSideProps:", error);
    return { notFound: true };
  }
};
